import {
  EVENT_TO_I18N_ID,
  LeadActivityEvents,
  LeadActivityModel,
  LeadActivityType,
} from '@/typings/models/LeadActivity';
import {
  BankOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DisconnectOutlined,
  EditOutlined,
  EuroCircleOutlined,
  ExperimentOutlined,
  FileAddOutlined,
  FileOutlined,
  FileTextOutlined,
  FlagOutlined,
  InboxOutlined,
  MessageOutlined,
  PhoneOutlined,
  PlusCircleOutlined,
  RetweetOutlined,
  StopOutlined,
  SwapOutlined,
  TagOutlined,
  UserDeleteOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import React from 'react';

/**
 * Semantic tone for an activity. Each tone maps to a single colour class in
 * the consuming stylesheet — we deliberately avoid per-event hex codes so the
 * palette stays consistent and convention-compliant (no inline styles).
 */
export type ActivityTone =
  | 'positive'
  | 'negative'
  | 'warning'
  | 'info'
  | 'accent'
  | 'neutral';

export interface ActivityPresentation {
  icon: React.ReactNode;
  tone: ActivityTone;
}

const NEUTRAL: ActivityPresentation = {
  icon: <SwapOutlined />,
  tone: 'neutral',
};

export const ACTIVITY_PRESENTATION: Partial<
  Record<LeadActivityType, ActivityPresentation>
> = {
  // Lifecycle
  [LeadActivityEvents.created]: {
    icon: <PlusCircleOutlined />,
    tone: 'positive',
  },
  [LeadActivityEvents.archived]: { icon: <InboxOutlined />, tone: 'neutral' },
  [LeadActivityEvents.unarchived]: {
    icon: <InboxOutlined />,
    tone: 'positive',
  },

  // Field / state changes
  [LeadActivityEvents.lead_state_changed]: {
    icon: <SwapOutlined />,
    tone: 'info',
  },
  [LeadActivityEvents.lead_priority_changed]: {
    icon: <FlagOutlined />,
    tone: 'warning',
  },
  [LeadActivityEvents.branch_changed]: { icon: <BankOutlined />, tone: 'info' },
  [LeadActivityEvents.product_group_changed]: {
    icon: <TagOutlined />,
    tone: 'neutral',
  },
  [LeadActivityEvents.customer_data_changed]: {
    icon: <UserOutlined />,
    tone: 'info',
  },
  [LeadActivityEvents.extra_field_changed]: {
    icon: <EditOutlined />,
    tone: 'neutral',
  },

  [LeadActivityEvents.order_volume_changed]: {
    icon: <EuroCircleOutlined />,
    tone: 'positive',
  },

  // Carat plannings (CLICKUP-86cb5zayy)
  [LeadActivityEvents.carat_planning_added]: {
    icon: <FileAddOutlined />,
    tone: 'positive',
  },
  [LeadActivityEvents.carat_planning_updated]: {
    icon: <FileOutlined />,
    tone: 'info',
  },
  // Carries no value: the planning now holds another customer's data.
  [LeadActivityEvents.carat_planning_reassigned]: {
    icon: <DisconnectOutlined />,
    tone: 'warning',
  },

  // Assignment
  [LeadActivityEvents.assignee_changed]: {
    icon: <UserSwitchOutlined />,
    tone: 'info',
  },
  [LeadActivityEvents.unassigned]: {
    icon: <UserDeleteOutlined />,
    tone: 'neutral',
  },

  // Appointments
  [LeadActivityEvents.appointment_requested]: {
    icon: <CalendarOutlined />,
    tone: 'info',
  },
  [LeadActivityEvents.appointment_booked]: {
    icon: <CheckCircleOutlined />,
    tone: 'positive',
  },
  [LeadActivityEvents.appointment_rescheduled]: {
    icon: <RetweetOutlined />,
    tone: 'warning',
  },
  [LeadActivityEvents.appointment_rescheduled_by_customer]: {
    icon: <RetweetOutlined />,
    tone: 'warning',
  },
  [LeadActivityEvents.appointment_start_time_changed]: {
    icon: <ClockCircleOutlined />,
    tone: 'warning',
  },
  [LeadActivityEvents.appointment_end_time_changed]: {
    icon: <ClockCircleOutlined />,
    tone: 'warning',
  },
  [LeadActivityEvents.appointment_cancelled]: {
    icon: <CloseCircleOutlined />,
    tone: 'negative',
  },
  [LeadActivityEvents.appointment_cancelled_by_customer]: {
    icon: <CloseCircleOutlined />,
    tone: 'negative',
  },
  [LeadActivityEvents.appointment_declined]: {
    icon: <StopOutlined />,
    tone: 'negative',
  },
  [LeadActivityEvents.appointment_assignee_changed]: {
    icon: <UserSwitchOutlined />,
    tone: 'info',
  },
  [LeadActivityEvents.appointment_branch_service_changed]: {
    icon: <TagOutlined />,
    tone: 'info',
  },
  [LeadActivityEvents.appointment_attendance_status_changed]: {
    icon: <CheckCircleOutlined />,
    tone: 'info',
  },

  // Calendar events (blockers)
  [LeadActivityEvents.calendar_event_created]: {
    icon: <CalendarOutlined />,
    tone: 'accent',
  },
  [LeadActivityEvents.calendar_event_updated]: {
    icon: <CalendarOutlined />,
    tone: 'accent',
  },
  [LeadActivityEvents.calendar_event_deleted]: {
    icon: <CalendarOutlined />,
    tone: 'neutral',
  },

  // Files
  [LeadActivityEvents.file_uploaded]: {
    icon: <FileAddOutlined />,
    tone: 'positive',
  },
  [LeadActivityEvents.file_deleted]: {
    icon: <FileOutlined />,
    tone: 'neutral',
  },
  [LeadActivityEvents.customer_file_uploaded]: {
    icon: <FileAddOutlined />,
    tone: 'accent',
  },
  [LeadActivityEvents.customer_file_deleted]: {
    icon: <FileOutlined />,
    tone: 'neutral',
  },

  // Communication
  [LeadActivityEvents.call]: { icon: <PhoneOutlined />, tone: 'info' },
  [LeadActivityEvents.call_back]: { icon: <PhoneOutlined />, tone: 'info' },
  [LeadActivityEvents.callback_from_location]: {
    icon: <PhoneOutlined />,
    tone: 'info',
  },
  [LeadActivityEvents.comment]: { icon: <MessageOutlined />, tone: 'accent' },

  // Notes written on a task (task automations)
  [LeadActivityEvents.task_note]: {
    icon: <FileTextOutlined />,
    tone: 'info',
  },

  // Integrations
  [LeadActivityEvents.trickpark_session_added]: {
    icon: <ExperimentOutlined />,
    tone: 'accent',
  },
};

export const getActivityPresentation = (
  type: LeadActivityType,
): ActivityPresentation => ACTIVITY_PRESENTATION[type] ?? NEUTRAL;

/**
 * The displayable text of one side of an extra-field change. The payload is
 * `{label, value}` (value is a scalar) but legacy records may carry the bare
 * scalar — never fall back to the label, it names the field, not the value.
 */
export const getExtraFieldValueText = (side: any): string => {
  const raw = side && typeof side === 'object' ? side.value : side;
  if (raw === null || raw === undefined || typeof raw === 'object') return '';
  return String(raw).trim();
};

/** Extra-field logs where both sides are blank carry no information — hide them. */
export const isEmptyExtraFieldChange = (activity: LeadActivityModel): boolean =>
  activity.activity_type === LeadActivityEvents.extra_field_changed &&
  !getExtraFieldValueText(activity.prev_value) &&
  !getExtraFieldValueText(activity.new_value);

/** "utm_source" → "Utm source" — readable fallback for keys without a translation. */
export const humanizeFieldKey = (key: string): string => {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * The card headline for an activity. Extra-field changes name the actual field
 * ("Budget updated"); everything else uses its translated event title, falling
 * back to a humanized event type instead of the raw key.
 */
export const getActivityTitle = (
  activity: LeadActivityModel,
  intl: {
    formatMessage: (
      descriptor: { id: string; defaultMessage: string },
      values?: Record<string, any>,
    ) => string;
  },
): string => {
  if (activity.activity_type === LeadActivityEvents.extra_field_changed) {
    const fieldLabel = activity.prev_value?.label || activity.new_value?.label;
    if (fieldLabel) {
      return intl.formatMessage(
        {
          id: 'pages.lead.activities.fieldChanged.title',
          defaultMessage: '{field} updated',
        },
        { field: fieldLabel },
      );
    }
  }

  const i18nKey = EVENT_TO_I18N_ID[activity.activity_type];
  return intl.formatMessage({
    id: i18nKey || activity.activity_type,
    defaultMessage: humanizeFieldKey(activity.activity_type),
  });
};
